import React, { useState, useEffect } from 'react';
import { getCurrentUser } from '../utils/userStorage';
import { PEEKPASS_BRAND } from '../utils/peekPassBrand';

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
  country: string;
  timezone: string;
}

interface FormErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  password?: string;
  confirmPassword?: string;
  country?: string;
  timezone?: string;
  general?: string;
}

interface RegistrationFormProps {
  onComplete: (data: FormData) => Promise<void> | void;
  onGoToLogin?: () => void;
}

const RegistrationForm: React.FC<RegistrationFormProps> = ({ onComplete, onGoToLogin }) => {
  const defaultCountry = 'Argentina';
  const defaultTimezone = 'America/Argentina/Buenos_Aires';

  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    country: defaultCountry,
    timezone: defaultTimezone
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 🔐 VERIFICAR SI YA HAY UNA SESIÓN ACTIVA Y LIMPIAR AUTCOMPLETADO
  useEffect(() => {
    const currentUser = getCurrentUser();
    if (currentUser) {
      setErrors({
        firstName: `Ya hay una sesión activa con el usuario: ${currentUser.email}. Por favor, cierra sesión antes de registrarte con otro usuario.`
      });
    }
    
    // 🧹 LIMPIAR CUALQUIER VALOR PREVIO EN LOS CAMPOS
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        confirmPassword: '',
        country: defaultCountry,
        timezone: defaultTimezone
      });
    
    // 🚫 LIMPIAR AUTCOMPLETADO DEL NAVEGADOR
    setTimeout(() => {
      const emailInput = document.getElementById('email') as HTMLInputElement;
      const passwordInput = document.getElementById('password') as HTMLInputElement;
      
      if (emailInput) {
        emailInput.value = '';
        emailInput.setAttribute('autocomplete', 'new-password');
      }
      if (passwordInput) {
        passwordInput.value = '';
        passwordInput.setAttribute('autocomplete', 'new-password');
      }
    }, 100);
  }, [defaultCountry, defaultTimezone]);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePassword = (password: string): boolean => password.length >= 6;

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Validar nombre
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'El nombre es requerido';
    }

    // Validar apellido
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'El apellido es requerido';
    }

    // Validar email
    if (!formData.email.trim()) {
      newErrors.email = 'El email es requerido';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'El formato del email no es válido';
    }

    if (!formData.password) {
      newErrors.password = 'La contraseña es requerida';
    } else if (!validatePassword(formData.password)) {
      newErrors.password = 'La contraseña debe tener al menos 6 caracteres';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Confirmá tu contraseña';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Las contraseñas no coinciden';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Limpiar error cuando el usuario empiece a escribir
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;
    
    // 🔐 VERIFICAR SI YA HAY UNA SESIÓN ACTIVA
    const currentUser = getCurrentUser();
    if (currentUser) {
      setErrors({
        firstName: `Ya hay una sesión activa con el usuario: ${currentUser.email}. Por favor, cierra sesión antes de registrarte con otro usuario.`
      });
      return;
    }
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setErrors({});
    
    try {
      // Llamar a la función de completado
      await onComplete(formData);
      
    } catch (error: any) {
      console.error('Error en el registro:', error);
      setErrors({
        general: error?.message || 'No pudimos completar el registro. Intentalo nuevamente.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[linear-gradient(180deg,#f8fafc_0%,#f2f4f7_100%)] px-4 py-10">
      <div className="mx-auto grid w-full max-w-6xl gap-6 lg:grid-cols-[minmax(0,1fr)_520px]">
        <div className="hidden rounded-[2.5rem] border border-slate-200 bg-white p-10 shadow-[0_34px_90px_-45px_rgba(15,23,42,0.22)] lg:block">
          <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-slate-500">{PEEKPASS_BRAND}</p>
          <h1 className="mt-6 text-5xl font-black tracking-[-0.06em] text-slate-950">Creá tu cuenta y empezá a vender experiencias.</h1>
          <p className="mt-5 max-w-xl text-lg leading-8 text-slate-600">
            Registrate para comprar entradas, publicar eventos y operar con una imagen premium.
          </p>
          <div className="mt-10 grid gap-4">
            <div className="rounded-[1.6rem] border border-slate-200 bg-slate-50 p-5">
              <p className="text-sm font-semibold text-slate-950">Organizadores</p>
              <p className="mt-2 text-sm text-slate-600">Publicá tu evento y vinculá Mercado Pago desde tu panel cuando estés listo.</p>
            </div>
            <div className="rounded-[1.6rem] border border-slate-200 bg-slate-50 p-5">
              <p className="text-sm font-semibold text-slate-950">Compradores</p>
              <p className="mt-2 text-sm text-slate-600">Comprá entradas, revisá estados de pago y recibí confirmaciones por email.</p>
            </div>
          </div>
        </div>

      <div className="w-full rounded-[2.2rem] border border-gray-200 bg-white p-8 shadow-[0_24px_70px_-38px_rgba(15,23,42,0.2)]">
        <div className="mb-8 text-center">
          <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-slate-500">{PEEKPASS_BRAND}</p>
          <h1 className="mb-2 mt-3 text-3xl font-bold tracking-[-0.04em] text-gray-950">Crear cuenta</h1>
          <p className="text-gray-500">Completá tus datos para registrarte</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Campos ocultos para confundir al autocompletado */}
          <input
            type="text"
            name="fake-email"
            autoComplete="off"
            style={{ display: 'none' }}
            tabIndex={-1}
          />
          <input
            type="password"
            name="fake-password"
            autoComplete="off"
            style={{ display: 'none' }}
            tabIndex={-1}
          />
          
          {errors.general && (
            <div className="rounded-2xl border border-red-200 bg-red-50 p-4">
              <p className="text-sm text-red-700">{errors.general}</p>
            </div>
          )}

          {/* Nombre y Apellido */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
                Nombre *
              </label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                className={`w-full rounded-2xl border px-4 py-3.5 transition-colors ${
                  errors.firstName ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Tu nombre"
              />
              {errors.firstName && (
                <p className="text-red-500 text-sm mt-1">{errors.firstName}</p>
              )}
            </div>

            <div>
              <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
                Apellido *
              </label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                className={`w-full rounded-2xl border px-4 py-3.5 transition-colors ${
                  errors.lastName ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Tu apellido"
              />
              {errors.lastName && (
                <p className="text-red-500 text-sm mt-1">{errors.lastName}</p>
              )}
            </div>
          </div>

          {/* Email */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
              Email *
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              autoComplete="new-password"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
              data-form-type="other"
              className={`w-full rounded-2xl border px-4 py-3.5 transition-colors ${
                errors.email ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="tu@email.com"
            />
            {errors.email && (
              <p className="text-red-500 text-sm mt-1">{errors.email}</p>
            )}
          </div>

          {/* Contraseña */}
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
              Contraseña *
            </label>
            <input
              type="password"
              id="password"
              name="password"
	                value={formData.password}
	                onChange={handleInputChange}
	                disabled={isSubmitting}
	                autoComplete="new-password"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
              data-form-type="other"
              className={`w-full rounded-2xl border px-4 py-3.5 transition-colors ${
                errors.password ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Mínimo 6 caracteres"
            />
	            {errors.password && (
	              <p className="text-red-500 text-sm mt-1">{errors.password}</p>
	            )}
	          </div>

	          {/* Confirmar contraseña */}
	          <div>
	            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
	              Confirmar contraseña *
	            </label>
	            <input
	              type="password"
	              id="confirmPassword"
	              name="confirmPassword"
	              value={formData.confirmPassword}
	              onChange={handleInputChange}
	              disabled={isSubmitting}
	              autoComplete="new-password"
	              autoCorrect="off"
	              autoCapitalize="off"
	              spellCheck="false"
	              data-form-type="other"
	              className={`w-full rounded-2xl border px-4 py-3.5 transition-colors ${
	                errors.confirmPassword ? 'border-red-500' : 'border-gray-300'
	              }`}
	              placeholder="Repetí tu contraseña"
	            />
	            {errors.confirmPassword && (
	              <p className="text-red-500 text-sm mt-1">{errors.confirmPassword}</p>
	            )}
	          </div>

          {/* Botón de envío */}
          <button
            type="submit"
            disabled={isSubmitting}
            className={`w-full rounded-2xl px-4 py-3.5 font-medium transition-colors ${
              isSubmitting
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-black hover:bg-slate-800 focus:ring-2 focus:ring-gray-900 focus:ring-offset-2'
            } text-white`}
          >
            {isSubmitting ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                Registrando...
              </div>
            ) : (
              'Continuar / Registrarme'
            )}
          </button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600">
            ¿Ya tienes cuenta?{' '}
            <button 
              type="button"
              className="cursor-pointer border-none bg-transparent font-medium text-gray-900 underline hover:text-gray-700"
              onClick={onGoToLogin}
            >
              Inicia sesión
            </button>
          </p>
        </div>
      </div>
      </div>
    </div>
  );
};

export default RegistrationForm;
